import { Component, OnInit, ViewChild } from '@angular/core';
import { CommunicationTemplateExampleComponent } from '@internal-components/administration-components/communication-template-components/communication-template-example-component/communication-template-example.component';
import { CustomCommunicationTemplateFormFactory } from '@internal-factories/forms/custom-communication-template-form.factory';
import {
	CommunicationChannel,
	CommunicationEndpoint,
	ICommunicationTemplate,
} from '@internal-models/communication-template.model';
import { CommunicationTemplateProvider } from '@internal-providers/communication-templates.provider';
import { EditorComponent } from '@internal/gui/structural-components/editor-component/editor.component';
import { Action, NavigationDataKey, NavigationType } from '@shared-libs/enums';
import { formIsValid, translate } from '@shared-libs/helpers';
import { IFormGroup } from '@shared-libs/interfaces';
import { MessageService } from '@shared-services/message.service';
import { NavigationService } from '@shared-services/navigation.service';
import { ValidationService } from '@shared-services/validation.service';
@Component({
	selector: 'communication-template-desktop-page',
	templateUrl: 'communication-template.desktop.page.html',
	styleUrls: ['./communication-template.desktop.page.scss'],
})
export class CommunicationTemplateDesktopPage implements OnInit {
	@ViewChild(EditorComponent) private readonly editor: EditorComponent;

	public type: 'custom' | 'standard' = 'standard';
	public action: Action;
	public template: ICommunicationTemplate;
	public templateForm: IFormGroup<ICommunicationTemplate>;
	public variables: Array<string>;

	public communicationChannels: Array<{ value: string; label: string }>;
	public communicationEndpoints: Array<{ value: string; label: string }>;

	public readonly Action: typeof Action = Action;
	public readonly CommunicationChannel: typeof CommunicationChannel = CommunicationChannel;

	constructor(
		private readonly provider: CommunicationTemplateProvider,
		private readonly navigationService: NavigationService,
		private readonly messageService: MessageService,
		private readonly validationService: ValidationService
	) {}

	public ngOnInit(): void {
		this.type = this.navigationService.getNavigationData(NavigationDataKey.type) ?? 'standard';
		this.template = this.navigationService.getNavigationData(NavigationDataKey.template);
		this.variables = this.navigationService.getNavigationData(NavigationDataKey.templateVariables);
		if (this.type === 'custom') {
			this.action = this.template.id ? Action.UPDATE : Action.CREATE;
			this.templateForm = CustomCommunicationTemplateFormFactory.create(this.template, this.action);
			this.getCommunicationChannels();
			this.getCommunicationEndpoints();
		} else {
			this.action = Action.UPDATE;
		}
	}

	public async openHtmlExample(): Promise<void> {
		const html = await this.provider.getHtmlExample(this.editor.getFormattedHtml(), this.template.messageType);
		await this.navigationService.navigateTo(NavigationType.modal, CommunicationTemplateExampleComponent, { html });
	}

	public async updateStandardTemplate(): Promise<void> {
		const html = this.editor.getFormattedHtml();
		await this.provider.updateStandard(this.template.id, { html });
		this.messageService.showSavedMessage();
	}

	public async createCustomTemplate(): Promise<void> {
		if (formIsValid(this.templateForm)) {
			const html = this.editor.getFormattedHtml();
			await this.provider.createCustom({
				...CustomCommunicationTemplateFormFactory.getValue(this.templateForm),
				html,
			});
			this.messageService.showSavedMessage();
			this.navigationService.navigateBack({ action: this.action });
		}
	}

	public async updateCustomTemplate(): Promise<void> {
		if (formIsValid(this.templateForm)) {
			const html = this.editor.getFormattedHtml();
			await this.provider.updateCustom(this.template.id, {
				...CustomCommunicationTemplateFormFactory.getValue(this.templateForm),
				html,
			});
			this.messageService.showSavedMessage();
		}
	}

	public async deleteCustomTemplate(): Promise<void> {
		if (await this.validationService.actionIsConfirmed('DELETE_CUSTOM_TEMPLATE')) {
			await this.provider.deleteCustom(this.template.id);
			this.navigationService.navigateBack({ action: this.action });
		}
	}

	/**
	 * Get the communication channels based on the {@link CommunicationChannel} enum
	 * formatted for the form-select
	 */
	private getCommunicationChannels(): void {
		this.communicationChannels = Object.keys(CommunicationChannel).map((key) => ({
			value: CommunicationChannel[key],
			label: CommunicationChannel[key],
		}));
	}

	/**
	 * Get the communication endpoints based on the {@link CommunicationEndpoint} enum
	 * formatted for the form-select
	 */
	private getCommunicationEndpoints(): void {
		this.communicationEndpoints = Object.keys(CommunicationEndpoint).map((key) => ({
			value: CommunicationEndpoint[key],
			label: translate('COMMUNICATION_ENDPOINTS.' + CommunicationEndpoint[key].toUpperCase()),
		}));
	}
}
